import { useState, useMemo, useCallback } from "react";
import { Tag, Tooltip, Modal, Button } from "antd";
import {
  EyeOutlined,
  EditOutlined,
  DeleteOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import {
  getMessageTemplates,
  deleteMessageTemplate,
  type MessageTemplate,
} from "@api/services/facebook";
import { TemplatePreview } from "@components/chat/ChatInput/TemplatePreview";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "@config/routes.config";
import { useNotificationContext } from "@hooks/useNotificationContext";
import {
  DataTable,
  type DataTableColumn,
  type DataTableAction,
} from "@components/ui";
import styles from "./styles.module.less";
import { getTypeLabel, getStatusLabel, getScopeLabel, getScopeColor, getTypeColor, getStatusColor } from "@constants/setting";

export const TemplateList = () => {
  const navigate = useNavigate();
  const { success, error: showError } = useNotificationContext();
  const [previewVisible, setPreviewVisible] = useState(false);
  const [previewTemplate, setPreviewTemplate] =
    useState<MessageTemplate | null>(null);

  const columns: DataTableColumn<MessageTemplate>[] = useMemo(
    () => [
      {
        title: "Tên mẫu",
        dataIndex: "name",
        key: "name",
        width: 300,
        render: (text: string) => (
          <Tooltip title={text}>
            <span className={styles.templateNameCell}>{text}</span>
          </Tooltip>
        ),
      },
      {
        title: "Loại",
        dataIndex: "type",
        key: "type",
        width: 120,
        render: (type: string) => {
          const color = getTypeColor(type);
          return <Tag color={color}>{getTypeLabel(type)}</Tag>;
        },
      },
      {
        title: "Phạm vi",
        dataIndex: "scope",
        key: "scope",
        width: 100,
        render: (scope: string) => (
          <Tag color={getScopeColor(scope)}>{getScopeLabel(scope)}</Tag>
        ),
      },
      {
        title: "Trạng thái",
        dataIndex: "status",
        key: "status",
        width: 100,
        render: (status: string) => (
          <Tag color={getStatusColor(status)}>{getStatusLabel(status)}</Tag>
        ),
      },
      {
        title: "Ngày cập nhật",
        dataIndex: "updatedAt",
        key: "updatedAt",
        width: 150,
        render: (date: string) => {
          if (!date) return "-";
          try {
            return new Date(date).toLocaleDateString("vi-VN", {
              year: "numeric",
              month: "2-digit",
              day: "2-digit",
              hour: "2-digit",
              minute: "2-digit",
            });
          } catch {
            return date;
          }
        },
      },
    ],
    []
  );

  const actions: DataTableAction<MessageTemplate>[] = useMemo(
    () => [
      {
        key: "preview",
        label: "Xem trước",
        icon: <EyeOutlined />,
        tooltip: "Xem trước",
        onClick: (record) => {
          setPreviewTemplate(record);
          setPreviewVisible(true);
        },
      },
      {
        key: "edit",
        label: "Chỉnh sửa",
        icon: <EditOutlined />,
        tooltip: "Chỉnh sửa",
        onClick: (record) => {
          const editUrl = ROUTES.SETTINGS.FACEBOOK_TEMPLATE_EDIT(record.id);
          navigate(editUrl);
        },
      },
      {
        key: "delete",
        label: "Xóa",
        icon: <DeleteOutlined />,
        tooltip: "Xóa",
        danger: true,
        confirm: {
          title: "Xóa mẫu tin nhắn",
          description: "Bạn có chắc muốn xóa mẫu tin nhắn này?",
          okText: "Xóa",
          cancelText: "Hủy",
        },
        onClick: async (record) => {
          try {
            const response = await deleteMessageTemplate(record.id);
            if (response.code === 0) {
              success(
                "Xóa mẫu template thành công",
                "Mẫu template đã được xóa thành công.",
                3
              );
            } else {
              showError(
                "Xóa mẫu template thất bại",
                response.message || "Không thể xóa mẫu template",
                4
              );
            }
          } catch (err) {
            console.error("Error deleting template:", err);
            showError(
              "Lỗi hệ thống",
              "Đã xảy ra lỗi khi xóa mẫu template. Vui lòng thử lại.",
              4
            );
          }
        },
      },
    ],
    [navigate, success, showError]
  );

  const handleClosePreview = () => {
    setPreviewVisible(false);
    setPreviewTemplate(null);
  };

  const handleCreate = () => {
    navigate(ROUTES.SETTINGS.FACEBOOK_TEMPLATE_CREATE);
  };

  const handleEdit = (template: MessageTemplate) => {
    const editUrl = ROUTES.SETTINGS.FACEBOOK_TEMPLATE_EDIT(template.id);
    navigate(editUrl);
  };

  const fetchTemplates = useCallback(
    async (params: {
      offset: number;
      limit: number;
      search?: string;
      type?: string;
      scope?: string;
      status?: string;
    }) => {
      const response = await getMessageTemplates({
        offset: params.offset,
        limit: params.limit,
        ...(params.search ? { name: params.search } : {}),
        ...(params.type ? { type: params.type } : {}),
        ...(params.scope ? { scope: params.scope } : {}),
        ...(params.status ? { status: params.status } : {}),
      });
      return response;
    },
    []
  );

  return (
    <div className={styles.templateList}>
      <DataTable<MessageTemplate>
        fetchFn={fetchTemplates}
        columns={columns}
        rowKey="id"
        actions={actions}
        headerActions={
          <Tooltip title="Tạo mẫu">
            <Button
              type="link"
              icon={<PlusOutlined />}
              onClick={handleCreate}
            />
          </Tooltip>
        }
        title="Danh sách mẫu template facebook"
        searchable={false}
        filters={[
          {
            key: "search",
            label: "Tìm kiếm",
            type: "search",
            placeholder: "Tìm kiếm template...",
            colProps: { xs: 24, sm: 12, md: 8, lg: 6 },
          },
          {
            key: "type",
            label: "Loại template",
            type: "select",
            placeholder: "Loại template",
            options: [
              { label: "Generic", value: "GENERIC" },
              { label: "Button", value: "BUTTON" },
              { label: "Media", value: "MEDIA" },
            ],
            colProps: { xs: 24, sm: 12, md: 8, lg: 6 },
          },
          {
            key: "scope",
            label: "Phạm vi",
            type: "select",
            placeholder: "Phạm vi",
            options: [
              { label: "Cá nhân", value: "USER" },
              { label: "Hệ thống", value: "ADMIN" },
            ],
            colProps: { xs: 24, sm: 12, md: 8, lg: 6 },
          },
          {
            key: "status",
            label: "Trạng thái",
            type: "select",
            placeholder: "Trạng thái",
            options: [
              { label: "Hoạt động", value: "ACTIVE" },
              { label: "Tắt", value: "INACTIVE" },
            ],
            colProps: { xs: 24, sm: 12, md: 8, lg: 6 },
          },
        ]}
        filterCardBordered={true}
        filterRowProps={{ gutter: [16, 16] }}
        filterColProps={{ xs: 24, sm: 12, md: 8, lg: 6 }}
        defaultPageSize={10}
        scrollable={true}
        scrollX="max-content"
        storageKey="facebook-template-list"
        sticky={true}
        emptyText="Không có template nào"
        className={styles.dataTable}
      />

      <Modal
        title={`Xem trước: ${previewTemplate?.name || ""}`}
        open={previewVisible}
        onCancel={handleClosePreview}
        footer={[
          <Button key="close" onClick={handleClosePreview}>
            Đóng
          </Button>,
          <Button
            key="edit"
            type="primary"
            onClick={() => {
              if (previewTemplate) {
                handleClosePreview();
                handleEdit(previewTemplate);
              }
            }}
          >
            Chỉnh sửa
          </Button>,
        ]}
        width={400}
        centered
        className={styles.previewModal}
      >
        {previewTemplate && <TemplatePreview template={previewTemplate} />}
      </Modal>
    </div>
  );
};
